// External Imports
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import {
  MdCheck,
  MdClose,
  MdDeleteOutline,
  MdEdit,
  MdRefresh,
} from "react-icons/md";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import DetailPair from "../UI/DetailPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { requestAdminAPI } from "../../api/posh-api";
import LinkPair from "../UI/LinkPair";
import SelectPair from "../UI/SelectPair";
import SignaturePad from "../UI/SignaturePad";
import ImageSelector from "../UI/ImageSelector";
import DetailList from "../UI/DetailList";
import { UtilityContext } from "../../context/util-context";
import { uploadBase64String } from "../../util/upload-base64";
import { useLocation } from "react-router-dom";
import { isoDateToString } from "../../util/format-date";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import PersonCard from "../UI/PersonCard";
import ImageCarousel from "../UI/ImageCarousel";
import Notes from "../UI/Notes";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * ClaimInfo Component
 * This component renders a form to add a service.
 * ========================================================================== */

export default function ClaimInfo() {
  const { navigate, setConfirm } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get("id");

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [claim, setClaim] = useState({});
  const [edit, setEdit] = useState(false);
  const [show, setShow] = useState({
    driver: false,
    incident: false,
    other: false,
    witness: false,
    police: false,
    injury: false,
    damage: false,
  });

  /* ====================== FETCH CLAIM ====================== */
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "claim",
        id: id,
      });
      if (response.vehicleId)
        response.vehicle = await requestAdminAPI("fetch", {
          table: "vehicle",
          id: response.vehicleId,
          columns: ["make { name }"],
        });
      if (response.bookingId)
        response.booking = await requestAdminAPI("fetch", {
          table: "booking",
          id: response.bookingId,
          columns: ["customer { firstName lastName phone id photo }"],
        });
      setClaim(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== EDIT CLAIM ====================== */
  const submitHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        id: claim.id,
        amountApproved: parseFloat(
          document.getElementById("amountApproved").value
        ),
        amountPaid: parseFloat(document.getElementById("amountPaid").value),
        adjusterName: document.getElementById("adjusterName").value,
        adjusterPhone: document.getElementById("adjusterPhone").value,
        adjusterEmail: document.getElementById("adjusterEmail").value,
        claimNumber: document.getElementById("claimNumber").value,
        status: document.getElementById("status").value,
        driverName: document.getElementById("driverName").value,
        driverPhone: document.getElementById("driverPhone").value,
        driverEmail: document.getElementById("driverEmail").value,
        driverAddress: document.getElementById("driverAddress").value,
        driverLicense: document.getElementById("driverLicense").value,
        provider: document.getElementById("provider").value,
        policy: document.getElementById("policy").value,
        incidentDate: document.getElementById("incidentDate").value,
        incidentTime: document.getElementById("incidentTime").value,
        mileage: parseInt(document.getElementById("mileage").value),
        weather: document.getElementById("weather").value,
        location: document.getElementById("location").value,
        description: document.getElementById("description").value,
        otherName: document.getElementById("otherName").value,
        otherPhone: document.getElementById("otherPhone").value,
        otherEmail: document.getElementById("otherEmail").value,
        otherAddress: document.getElementById("otherAddress").value,
        otherLicense: document.getElementById("otherLicense").value,
        otherProvider: document.getElementById("otherProvider").value,
        otherPolicy: document.getElementById("otherPolicy").value,
        otherVehicle: document.getElementById("otherVehicle").value,
        otherVehicleLicense: document.getElementById("otherVehicleLicense")
          .value,
        otherVehicleColor: document.getElementById("otherVehicleColor").value,
        witnessName: document.getElementById("witnessName").value,
        witnessPhone: document.getElementById("witnessPhone").value,
        witnessStatement: document.getElementById("witnessStatement").value,
        reportNumber: document.getElementById("reportNumber").value,
        officerName: document.getElementById("officerName").value,
        injuries: document.getElementById("injuries").value === "Yes",
        injuryDetails: document.getElementById("injuryDetails").value,
        medical: document.getElementById("medical").value === "Yes",
        repairName: document.getElementById("repairName").value,
        cost: parseFloat(document.getElementById("cost").value),
        damageDetails: document.getElementById("damageDetails").value,
        toDelete: false,
      };

      // Upload Police Report
      const policeReport = document.getElementById("report").files[0];
      if (policeReport)
        payload.report = await uploadImageToFirebase(
          `vehicles/${claim.vehicleId}/claims/report.png`,
          "Police Report",
          policeReport,
          setUploading
        );

      // Upload Receipts
      const receipts = document.getElementById("receipts").files;
      if (receipts.length > 0) {
        const receiptsArray = [];
        for (let i = 0; i < receipts.length; i++) {
          const receipt = receipts[i];
          const url = await uploadImageToFirebase(
            `vehicles/${claim.vehicleId}/claims/receipts/${receipt.name}`,
            "Receipts",
            receipt,
            setUploading
          );
          receiptsArray.push(url);
        }
        payload.receipts = claim.receipts || [];
        payload.receipts.push(...receiptsArray);
      }

      // Upload Damage Photos
      const images = document.getElementById("images").files;
      if (images.length > 0) {
        const imagesArray = [];
        for (let i = 0; i < images.length; i++) {
          const image = images[i];
          const url = await uploadImageToFirebase(
            `vehicles/${claim.vehicleId}/claims/damage/${image.name}`,
            "Damage Photos",
            image,
            setUploading
          );
          imagesArray.push(url);
        }
        payload.images = claim.images || [];
        payload.images.push(...imagesArray);
      }

      // Upload Resolution Letter
      const letter = document.getElementById("letter").files[0];
      if (letter) {
        const url = await uploadImageToFirebase(
          `vehicles/${claim.vehicleId}/claims/letter/${letter.name}`,
          "Resolution Letter",
          letter,
          setUploading
        );
        payload.letter = url;
      }

      // Send Request
      await requestAdminAPI("edit-claim", payload);

      setEdit(false);
      fetchRecord();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== DELETE CLAIM ====================== */
  const deleteHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("edit-claim", {
        id: claim.id,
        toDelete: true,
      });
      navigate("/claims");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== DOWNLOAD CLAIM FORM ====================== */
  const downloadForm = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("get-claim-form", {
        claimId: claim.id,
      });
      window.open(res, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
  }, [id]);

  return (
    <div className="divider">
      <div className="container gap-8 lg:col-span-3">
        <Heading
          title="Claim Information"
          text="Click on the heading to expand the section. New Documents can be added by clicking the pencil icon on the top right, but older documents cannot be deleted. Use Notes at the bottom to add additional information."
          id={id}
        >
          <AuditLogs table="claim" id={id} />
          <MdRefresh className="icon" onClick={fetchRecord} />
        </Heading>
        <div className="info">
          <DetailPair
            edit={true}
            label="Vehicle"
            inputOptions={{
              disabled: true,
              type: "text",
              id: "vehicleId",
              value: `${claim.vehicle?.year} ${claim.vehicle?.make.name} ${
                claim.vehicle?.model
              } ${claim?.vehicle?.vin.substring(13, 17)}`,
            }}
            loading={loading}
          />
          <DetailPair
            edit={true}
            label="Booking ID"
            inputOptions={{
              disabled: true,
              type: "text",
              id: "bookingId",
              value: claim?.bookingId,
            }}
            loading={loading}
          />
          <DetailPair
            edit={edit}
            label="Claim Number"
            value={claim?.claimNumber}
            inputOptions={{
              type: "text",
              id: "claimNumber",
              defaultValue: claim?.claimNumber,
            }}
          />
          <LinkPair
            edit={edit}
            label="Resolution Letter"
            link={claim?.letter}
            inputOptions={{
              type: "file",
              id: "letter",
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Adjuster"
              value={claim?.adjusterName}
              inputOptions={{
                type: "text",
                id: "adjusterName",
                defaultValue: claim?.adjusterName,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Phone Number"
            value={claim?.adjusterPhone}
            inputOptions={{
              type: "text",
              id: "adjusterPhone",
              defaultValue: claim?.adjusterPhone,
            }}
          />
          <DetailPair
            edit={edit}
            label="Email Address"
            value={claim?.adjusterEmail}
            inputOptions={{
              type: "text",
              id: "adjusterEmail",
              defaultValue: claim?.adjusterEmail,
            }}
          />
        </div>
        <Heading
          title="Driver Information"
          isBackArrow={false}
          onClick={() => setShow({ ...show, driver: !show.driver })}
        />

        <div className={`${show.driver ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Full Name"
              value={claim?.driverName}
              inputOptions={{
                type: "text",
                id: "driverName",
                defaultValue: claim?.driverName,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Phone Number"
            value={claim?.driverPhone}
            inputOptions={{
              type: "text",
              id: "driverPhone",
              defaultValue: claim?.driverPhone,
            }}
          />
          <DetailPair
            edit={edit}
            label="Email Address"
            value={claim?.driverEmail}
            inputOptions={{
              type: "text",
              id: "driverEmail",
              defaultValue: claim?.driverEmail,
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              edit={edit}
              label="Full Address"
              value={claim?.driverAddress}
              inputOptions={{
                type: "text",
                id: "driverAddress",
                defaultValue: claim?.driverAddress,
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Driver's License Number"
              value={claim?.driverLicense}
              inputOptions={{
                type: "text",
                id: "driverLicense",
                defaultValue: claim?.driverLicense,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Insurance Provider"
            value={claim?.provider}
            inputOptions={{
              type: "text",
              id: "provider",
              defaultValue: claim?.provider,
            }}
          />
          <DetailPair
            edit={edit}
            label="Policy Number"
            value={claim?.policy}
            inputOptions={{
              type: "text",
              id: "policy",
              defaultValue: claim?.policy,
            }}
          />
        </div>

        <Heading
          title="Incident Details"
          isBackArrow={false}
          onClick={() => setShow({ ...show, incident: !show.incident })}
        />

        <div className={`${show.incident ? "" : "hidden"} info`}>
          <DetailPair
            edit={edit}
            label="Incident Date"
            value={isoDateToString(claim?.incidentDate)}
            inputOptions={{
              type: "date",
              id: "incidentDate",
              defaultValue: isoDateToString(claim?.incidentDate, "yyyy-MM-dd"),
            }}
          />
          <DetailPair
            edit={edit}
            label="Incident Time"
            value={isoDateToString(claim?.incidentTime)}
            inputOptions={{
              type: "time",
              id: "incidentTime",
              defaultValue: isoDateToString(claim?.incidentTime, "hh:mm a"),
            }}
          />
          <DetailPair
            edit={edit}
            label="Vehicle Mileage"
            value={claim?.mileage}
            inputOptions={{
              type: "text",
              id: "mileage",
              defaultValue: claim?.mileage,
            }}
          />
          <DetailPair
            edit={edit}
            label="Weather Conditions"
            value={claim?.weather}
            inputOptions={{
              type: "text",
              id: "weather",
              defaultValue: claim?.weather,
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              edit={edit}
              label="Incident Location"
              value={claim?.location}
              inputOptions={{
                type: "text",
                id: "location",
                defaultValue: claim?.location,
              }}
            />
          </div>
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">
              Incident Description:
            </label>
            <textarea
              required={true}
              type="text"
              id="description"
              placeholder="Describe what happened..."
              rows={4}
              className="form-input"
              defaultValue={claim?.description}
            />
          </div>
        </div>

        <Heading
          title="Other Party"
          isBackArrow={false}
          onClick={() => setShow({ ...show, other: !show.other })}
        />

        <div className={`${show.other ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Full Name"
              value={claim?.otherName}
              inputOptions={{
                type: "text",
                id: "otherName",
                defaultValue: claim?.otherName,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Phone Number"
            value={claim?.otherPhone}
            inputOptions={{
              type: "text",
              id: "otherPhone",
              defaultValue: claim?.otherPhone,
            }}
          />
          <DetailPair
            edit={edit}
            label="Email Address"
            value={claim?.otherEmail}
            inputOptions={{
              type: "text",
              id: "otherEmail",
              defaultValue: claim?.otherEmail,
            }}
          />
          <div className="lg:col-span-4">
            <DetailPair
              edit={edit}
              label="Full Address"
              value={claim?.otherAddress}
              inputOptions={{
                type: "text",
                id: "otherAddress",
                defaultValue: claim?.otherAddress,
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Driver's License Number"
              value={claim?.otherLicense}
              inputOptions={{
                type: "text",
                id: "otherLicense",
                defaultValue: claim?.otherLicense,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Insurance Provider"
            value={claim?.otherProvider}
            inputOptions={{
              type: "text",
              id: "otherProvider",
              defaultValue: claim?.otherProvider,
            }}
          />
          <DetailPair
            edit={edit}
            label="Policy Number"
            value={claim?.otherPolicy}
            inputOptions={{
              type: "text",
              id: "otherPolicy",
              defaultValue: claim?.otherPolicy,
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Vehicle Make, Model, Year"
              value={claim?.otherVehicle}
              inputOptions={{
                type: "text",
                id: "otherVehicle",
                defaultValue: claim?.otherVehicle,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Vehicle Color"
            value={claim?.otherVehicleColor}
            inputOptions={{
              type: "text",
              id: "otherVehicleColor",
              defaultValue: claim?.otherVehicleColor,
            }}
          />
          <DetailPair
            edit={edit}
            label="Vehicle License"
            value={claim?.otherVehicleLicense}
            inputOptions={{
              type: "text",
              id: "otherVehicleLicense",
              defaultValue: claim?.otherVehicleLicense,
            }}
          />
        </div>

        <Heading
          title="Witness Information"
          isBackArrow={false}
          onClick={() => setShow({ ...show, witness: !show.witness })}
        />
        <div className={`${show.witness ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Full Name"
              value={claim?.witnessName}
              inputOptions={{
                type: "text",
                id: "witnessName",
                defaultValue: claim?.witnessName,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Phone Number"
            value={claim?.witnessPhone}
            inputOptions={{
              type: "text",
              id: "witnessPhone",
              defaultValue: claim?.witnessPhone,
            }}
          />
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">Witness Statement:</label>
            <textarea
              type="text"
              id="witnessStatement"
              placeholder="What did the witness say..."
              rows={4}
              className="form-input"
              defaultValue={claim?.witnessStatement}
            />
          </div>
        </div>

        <Heading
          title="Police Report"
          isBackArrow={false}
          onClick={() => setShow({ ...show, police: !show.police })}
        />

        <div className={`${show.police ? "" : "hidden"} info`}>
          <DetailPair
            edit={edit}
            label="Report Number"
            value={claim?.reportNumber}
            inputOptions={{
              type: "text",
              id: "reportNumber",
              defaultValue: claim?.reportNumber,
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Officer Full Name"
              value={claim?.officerName}
              inputOptions={{
                type: "text",
                id: "officerName",
                defaultValue: claim?.officerName,
              }}
            />
          </div>
          <LinkPair
            edit={edit}
            label="Police Report"
            link={claim?.report}
            inputOptions={{
              type: "file",
              accept: "image/*, .pdf",
              id: "report",
            }}
          />
        </div>

        <Heading
          title="Injury and Medical"
          isBackArrow={false}
          onClick={() => setShow({ ...show, injury: !show.injury })}
        />
        <div className={`${show.injury ? "" : "hidden"} info`}>
          <SelectPair
            edit={edit}
            label="Injuries"
            value={claim?.injuries ? "Yes" : "No"}
            options={["Yes", "No"]}
            inputOptions={{
              id: "injuries",
              defaultValue: claim?.injuries ? "Yes" : "No",
            }}
          />
          <SelectPair
            edit={edit}
            label="Medical Treatment"
            options={["Yes", "No"]}
            value={claim?.medical ? "Yes" : "No"}
            inputOptions={{
              id: "medical",
              defaultValue: claim?.medical ? "Yes" : "No",
            }}
          />
          {edit ? (
            <LinkPair
              edit={edit}
              label="Receipts"
              inputOptions={{
                type: "file",
                id: "receipts",
                accept: "image/*, .pdf",
                multiple: true,
              }}
            />
          ) : (
            <div className="lg:col-span-2">
              <p className="text-stone-400">Receipts:</p>
              <div className="flex space-x-4 text-sm text-primary">
                {claim?.receipts?.map((receipt, idx) => (
                  <a
                    key={idx}
                    href={receipt}
                    target="_blank"
                    className="hover:underline"
                  >
                    Document {idx + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">Injury Details:</label>
            <textarea
              type="text"
              id="injuryDetails"
              placeholder="Describe the injuries..."
              rows={2}
              className="form-input"
              defaultValue={claim?.injuryDetails}
            />
          </div>
        </div>

        <Heading
          title="Vehicle Damage"
          isBackArrow={false}
          onClick={() => setShow({ ...show, damage: !show.damage })}
        />
        <div className={`${show.damage ? "" : "hidden"} info`}>
          <div className="lg:col-span-2">
            <DetailPair
              edit={edit}
              label="Report Shop Name"
              value={claim?.repairName}
              inputOptions={{
                type: "text",
                id: "repairName",
                defaultValue: claim?.repairName,
              }}
            />
          </div>
          <DetailPair
            edit={edit}
            label="Damage Amount"
            value={claim?.cost}
            inputOptions={{
              type: "number",
              step: "0.01",
              min: "0",
              id: "cost",
              defaultValue: claim?.cost,
            }}
          />
          {edit ? (
            <LinkPair
              edit={edit}
              label="Damage Photos"
              inputOptions={{
                type: "file",
                accept: "image/*",
                multiple: true,
                id: "images",
              }}
            />
          ) : (
            <div className="lg:col-span-4">
              <ImageCarousel images={claim?.images} preview={true} />
            </div>
          )}
          <div className="grid lg:col-span-4">
            <label className="text-sm text-stone-400">Damage Details:</label>
            <textarea
              type="text"
              id="damageDetails"
              placeholder="Describe the damage to vehicle..."
              rows={2}
              className="form-input"
              defaultValue={claim?.damageDetails}
            />
          </div>
        </div>

        <Notes table="claim" id={claim.id} />
      </div>
      <div className="sidebar">
        <div className="grid gap-6">
          <div className="flex justify-around space-x-4">
            {edit ? (
              <Fragment>
                <MdClose className="icon" onClick={() => setEdit(false)} />
                <MdCheck className="icon" onClick={submitHandler} />
              </Fragment>
            ) : (
              <Fragment>
                <MdEdit className="icon" onClick={() => setEdit(true)} />
              </Fragment>
            )}
          </div>
          {edit ? (
            <SelectPair
              label="Claim Status"
              options={["active", "denied", "completed"]}
              edit={true}
              inputOptions={{
                id: "status",
                defaultValue: claim.status,
              }}
            />
          ) : (
            <div className="grid mx-auto text-center">
              <p className="text-sm text-stone-400">Claim Status</p>
              <p
                className={`${
                  claim?.status === "active"
                    ? "text-yellow-600"
                    : claim?.status === "denied"
                    ? "text-red-600"
                    : "text-green-600"
                } text-lg font-bold`}
              >
                {claim.status?.toUpperCase()}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <DetailPair
              label="Amount Approved"
              edit={edit}
              value={`$${claim.amountApproved || 0}`}
              classes="font-semibold"
              inputOptions={{
                type: "number",
                step: "0.01",
                id: "amountApproved",
                defaultValue: claim.amountApproved,
              }}
            />
            <DetailPair
              label="Amount Paid"
              edit={edit}
              value={`$${claim.amountPaid || 0}`}
              classes="font-semibold"
              inputOptions={{
                type: "number",
                step: "0.01",
                id: "amountPaid",
                defaultValue: claim.amountPaid,
              }}
            />
          </div>
          {claim.booking?.customer && (
            <PersonCard
              label="Driver:"
              name={`${claim.booking.customer.firstName} ${claim.booking.customer.lastName}`}
              secondary={claim.booking.customer.phone}
              link={`/customers/info?id=${claim.booking.customer.id}`}
              image={claim.booking.customer.photo}
              loading={loading}
            />
          )}
          {claim.vehicle && (
            <div
              onClick={(e) =>
                navigate(`/vehicles/info?id=${claim.vehicleId}`, e)
              }
              className="grid gap-4 clickable"
            >
              <ImageSelector
                image={claim.vehicle?.cornerImage}
                classes="w-full min-h-[100px]"
                loading={loading}
              />
              <DetailList
                label="Vehicle Details"
                values={[
                  `${claim.vehicle?.year} ${claim.vehicle?.make?.name} ${claim.vehicle?.model}`,
                  `${claim.vehicleId} - ${claim.vehicle?.license} ${claim.vehicle?.licenseState}`,
                ]}
                loading={loading}
              />
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid gap-2">
            <LoadingSpinnerWrapper />
            {uploading && (
              <p className="text-sm text-center text-stone-400">
                Uploading {uploading[0]}... {uploading[1]}%
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            <div onClick={downloadForm} className="btn-secondary">
              Download Report
            </div>
            <div
              className="btn-warning"
              onClick={() =>
                setConfirm({
                  message:
                    "This action cannot be undone. All information regarding this claim will be lost.",
                  action: deleteHandler,
                })
              }
            >
              Delete Claim
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
